import { z } from 'zod/v4';

/**
 * Raw amfori BSCI `Monitoring` response shape (the audit-relevant subset the
 * sync consumes). Field names follow discovery §1/§4; the schema is deliberately
 * tolerant (nullable, never-throwing date coercion) so a single drifted field
 * never kills a whole monitoring. Tighten once the smoke script confirms the
 * exact live shape.
 *
 * Engineering notes (discovery §1):
 *  - percentages are `z.number()` (int in v2, float in v1) — never `.int()`.
 *  - dates arrive as ISO with or without a timezone; coerced to `Date | null`.
 */
/** `string | null`, tolerant of a missing key (→ null). Required output key. */
const nullableString = z.string().nullable().catch(null).default(null);

/** `number | null`, tolerant of a missing key (→ null). Required output key. */
const nullableNumber = z.number().nullable().catch(null).default(null);

/** Accepts ISO string / epoch / Date / null / missing → `Date | null`, never throws. */
const tolerantDate = z
  .union([z.string(), z.number(), z.date(), z.null(), z.undefined()])
  .default(null)
  .transform(v => {
    if (v === null || v === undefined || v === '') return null;
    const date = v instanceof Date ? v : new Date(v);
    return Number.isNaN(date.getTime()) ? null : date;
  });

/**
 * amfori encodes `goodPractices` as a JSON-wrapped string (`{"value":"…"}`),
 * not a plain string (confirmed via the live smoke test). Unwrap it to the
 * inner value; tolerate a plain string or null/missing.
 */
const goodPractices = z
  .string()
  .nullable()
  .catch(null)
  .transform(v => {
    if (v == null || v === '') return null;
    try {
      const parsed: unknown = JSON.parse(v);
      if (parsed && typeof parsed === 'object' && 'value' in parsed) {
        const inner = (parsed as { value: unknown }).value;
        return inner == null ? null : String(inner);
      }
    } catch {
      // Not JSON — fall through and keep the raw string.
    }
    return v;
  })
  .default(null);

/**
 * `.loose()` preserves every other section field amfori returns so
 * `digitized_audits.extracted_sections.pa_scores` keeps the full per-PA payload
 * for a future scorecard UI (D-010 "store as-is"). `sectionName` (e.g.
 * "PA1: Social Management System") is now consumed by the Audit Content tab.
 */
export const MonitoringContentSectionSchema = z
  .object({
    sectionCode: nullableString,
    sectionName: nullableString,
    sectionLabel: nullableString,
    sectionPercentage: nullableNumber,
    goodPractices,
  })
  .loose();

/**
 * Site postal address amfori embeds on each monitoring (also on the `/site`
 * resource). Surfaced as the Audit Content tab's "Site Details" location block.
 * Tolerant: any missing key collapses to null rather than failing the parse.
 */
export const MonitoringSiteAddressSchema = z
  .object({
    street: nullableString,
    city: nullableString,
    zip: nullableString,
    country: nullableString,
    countryName: nullableString,
    stateProvince: z
      .object({ name: nullableString, code: nullableString })
      .loose()
      .nullable()
      .catch(null)
      .default(null),
  })
  .loose()
  .nullable()
  .catch(null)
  .default(null);

export const MonitoringSchema = z.object({
  monitoringId: z.string(),
  initiative: nullableString,
  monitoringActivityCode: nullableString,
  /** e.g. "FULL_MONITORING" → "Monitoring Type" row (humanized for display). */
  monitoringType: nullableString,
  /** e.g. "SEMI_ANNOUNCED" → "Announcement Type" row (humanized for display). */
  announcementType: nullableString,
  submissionDate: tolerantDate,
  expirationDate: tolerantDate,
  /** Requested audit-window bounds (ISO date strings) → Monitoring Start/End Date rows. */
  requestedTimeWindowFromDate: nullableString,
  requestedTimeWindowToDate: nullableString,
  monitoringPartnerLegalName: nullableString,
  /** Free-text auditor narrative — surfaced as the audit's "General notes". */
  generalDescription: nullableString,
  overallRatingLabel: nullableString,
  overallRatingPercentage: nullableNumber,
  monitoringContent: z.array(MonitoringContentSectionSchema).default([]),
  siteAmforiId: z.string(),
  /** Audited party's amfori id → Audit Content "amfori ID" row. */
  businessPartnerAmforiId: nullableString,
  siteAddress: MonitoringSiteAddressSchema,
  memberAmforiId: nullableString,
});

export type Monitoring = z.infer<typeof MonitoringSchema>;
export type MonitoringContentSection = z.infer<
  typeof MonitoringContentSectionSchema
>;
